import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import { LineIcon } from '../../ui/icons/LineIcon.js';
import { cn } from '../../../lib/utils.js';

const Checkbox = forwardRef(
  ({
    variants = "default",
    label,
    disabled = false,
    id,
    value,
    weight = "medium",
    desc,
    direction = "right",
    ...props
  }, ref) => {
    const handleKeyDown = (e) => {
      if (e.key === "" || e.code === "Space") {
        e.preventDefault();
        const checkbox = e.currentTarget.querySelector(
          'input[type="checkbox"]'
        );
        if (checkbox && !disabled) {
          checkbox.click();
        }
      }
    };
    return /* @__PURE__ */ jsxs(
      "label",
      {
        htmlFor: id,
        className: cn(
          "group flex items-start gap-2 cursor-pointer",
          variants === "card" && "relative border border-basic-04 rounded-lg p-4 has-[:checked]:!border-primary-02",
          direction === "left" && "flex-row-reverse"
        ),
        onKeyDown: handleKeyDown,
        children: [
          /* @__PURE__ */ jsxs("span", { className: cn("relative", variants === "card" && "hidden"), children: [
            /* @__PURE__ */ jsx(
              "input",
              {
                type: "checkbox",
                ref,
                id,
                value,
                disabled,
                ...props,
                className: cn(
                  "peer relative w-6 h-6 grid bg-base-white border border-basic-01 rounded-sm place-content-center shrink-0 appearance-none outline-none",
                  "checked:bg-interactive-active-selected-02 checked:border-none",
                  "checked:disabled:bg-primary-background-06 checked:disabled:border-primary-04",
                  "disabled:bg-transparent-white disabled:border-interactive-active-disabled-01",
                  "focus-visible:outline-black focus-visible:!outline-[2px]  !outline-offset-2",
                  variants === "icon" && "!bg-transparent-white !border-none"
                )
              }
            ),
            /* @__PURE__ */ jsx(
              "span",
              {
                className: cn(
                  variants === "icon" ? "text-neutral-600 peer-checked:text-primary-04 peer-disabled:text-state-disabled-03 [.peer:checked:disabled~&]:!text-state-disabled-05" : "text-base-white hidden peer-checked:block"
                ),
                children: /* @__PURE__ */ jsx(
                  LineIcon,
                  {
                    icoColor: "bg-icon-basic-06",
                    icoName: "ico-checkbox",
                    type: "xs",
                    className: cn(
                      "ico-chk absolute top-[4px] left-[4px]",
                      variants === "icon" && "bg-icon-basic-04  group-has-[:checked]:bg-icon-interactive-active-selected-02 group-has-[:disabled]:bg-icon-interactive-active-disabled-02"
                    )
                  }
                )
              }
            )
          ] }),
          label && typeof label === "string" ? /* @__PURE__ */ jsxs("span", { className: "flex flex-col gap-0.5 pt-[1px]", children: [
            /* @__PURE__ */ jsx(
              "span",
              {
                className: cn(
                  "label group-has-[:checked]:text-primary-04 group-has-[:disabled]:text-state-disabled-04 text-basic-04",
                  variants === "icon" ? "body-md pt-0.5 group-has-[:checked]:font-weight-medium" : "body-lg",
                  weight === "bold" && "font-weight-bold",
                  weight === "bold" && variants === "default" && "font-weight-bold text-basic-02",
                  weight === "medium" && "font-weight-medium",
                  weight === "light" && "font-weight-light"
                ),
                children: label
              }
            ),
            desc && /* @__PURE__ */ jsx("p", { className: "body-md text-basic-04 group-has-[:checked]:text-primary-04", children: desc })
          ] }) : label
        ]
      }
    );
  }
);

export { Checkbox };
